// src/components/ShoppingView.ts
// Aisle-grouped interactive shopping list with dual modes (Shop vs Plan) and "Finalise Shopping List" workflow

import type { AppState, ShoppingList, ListItem, KnownItem } from '../types';
import { api } from '../services/api';
import { syncManager } from '../services/sync';
import { MealPlanCard } from './MealPlanCard';
import { showToast } from './Toast';

const AISLE_ORDER = [
  'Fruit & Veg',
  'Meat & Fish',
  'Dairy & Eggs',
  'Bakery',
  'Frozen',
  'Tins & Jars',
  'Pasta & Rice',
  'Snacks',
  'Drinks',
  'Condiments',
  'Cleaning',
  'Toiletries',
  'Baby',
  'Pet',
  'General',
  'Other',
];

export class ShoppingView {
  private el: HTMLElement;
  private mealPlanCard: MealPlanCard | null = null;
  private selectedSuggestionIdx = -1;
  private filteredSuggestions: KnownItem[] = [];
  private activeMode: 'shop' | 'plan' = 'shop';

  constructor(
    private state: AppState,
    private callbacks: {
      onBack: () => void;
      onListUpdated: () => void;
    }
  ) {
    this.el = document.createElement('div');
    this.el.className = 'main-content';
    this.render();
  }

  public getElement(): HTMLElement {
    return this.el;
  }

  public update(state: AppState) {
    this.state = state;
    this.render();
  }

  private render() {
    const list = this.state.currentList;
    if (!list) {
      this.el.innerHTML = `<div style="text-align:center;padding:2rem;">No list selected.</div>`;
      return;
    }

    const { currentHousehold, user } = this.state;
    const isOwnerOrEditor = currentHousehold?.role === 'owner' || currentHousehold?.role === 'editor';
    const isShopperOnly = currentHousehold?.role === 'shopper';

    // If shopper only, locked into 'shop' mode
    if (isShopperOnly) {
      this.activeMode = 'shop';
    }

    const items = list.items || [];
    const total = items.length;
    const done = items.filter((i) => i.checked).length;
    const skipped = items.filter((i) => i.skipped);
    const pending = items.filter((i) => !i.checked && !i.skipped);
    const checked = items.filter((i) => i.checked);

    const dateStr = new Date(list.createdAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    // Mode Toggle Selector for Owner & Editor
    const modeToggleHtml = isOwnerOrEditor
      ? `
        <div class="view-mode-toggle">
          <button class="view-mode-btn ${this.activeMode === 'shop' ? 'active' : ''}" id="mode-shop-btn" title="In-store shopping mode">
            🛒 Shop Mode
          </button>
          <button class="view-mode-btn ${this.activeMode === 'plan' ? 'active' : ''}" id="mode-plan-btn" title="Planning & editing mode">
            📝 Plan &amp; Edit
          </button>
        </div>
      `
      : '';

    // Status Banner
    let bannerHtml = '';
    if (list.shopDone && skipped.length > 0) {
      bannerHtml = `
        <div style="background:var(--amber-light);border:1px solid var(--amber-border);border-radius:var(--radius-md);padding:0.75rem 1rem;display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <span style="font-size:1.5rem;">⚠️</span>
          <div>
            <div style="font-weight:600;font-size:0.95rem;color:var(--amber);">Shop completed with ${skipped.length} missed item${skipped.length !== 1 ? 's' : ''}</div>
            <div style="font-size:0.8rem;color:var(--ink-secondary);">Flagged below for review</div>
          </div>
        </div>
      `;
    } else if (total > 0 && done === total && !skipped.length) {
      bannerHtml = `
        <div style="background:var(--sage-light);border:1px solid var(--sage-border);border-radius:var(--radius-md);padding:0.75rem 1rem;display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <span style="font-size:1.5rem;">🎉</span>
          <div>
            <div style="font-weight:600;font-size:0.95rem;color:var(--sage);">All items collected!</div>
            <div style="font-size:0.8rem;color:var(--ink-secondary);">Ready to check out</div>
          </div>
        </div>
      `;
    } else if (list.readyToShop && !list.shopDone) {
      bannerHtml = `
        <div style="background:var(--sage-light);border:1px solid var(--sage-border);border-radius:var(--radius-md);padding:0.75rem 1rem;display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <span style="font-size:1.5rem;">🛒</span>
          <div>
            <div style="font-weight:600;font-size:0.95rem;color:var(--sage);">List Finalised &amp; Ready for Shopping</div>
            <div style="font-size:0.8rem;color:var(--ink-secondary);">
              ${list.finalizedByName ? `Finalised by ${escapeHtml(list.finalizedByName)} • Shoppers notified` : 'All items are ready to collect'}
            </div>
          </div>
        </div>
      `;
    }

    // Aisle grouping for pending items
    const groups: Record<string, ListItem[]> = {};
    pending.forEach((item) => {
      const a = item.aisle || 'General';
      if (!groups[a]) groups[a] = [];
      groups[a].push(item);
    });

    const sortedAisles = Object.keys(groups).sort((a, b) => {
      const ai = AISLE_ORDER.indexOf(a);
      const bi = AISLE_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    let itemsHtml = '';
    if (total === 0) {
      itemsHtml = `
        <div style="background:var(--bg-card);border:1px dashed var(--border);border-radius:var(--radius-lg);padding:2.5rem 1.5rem;text-align:center;margin-top:1rem;">
          <div style="font-size:2.2rem;margin-bottom:0.4rem;">📝</div>
          <div style="font-weight:600;font-size:1.05rem;margin-bottom:0.25rem;">This list is empty</div>
          <div style="font-size:0.85rem;color:var(--ink-secondary);">
            ${isOwnerOrEditor ? 'Add items using the box above.' : 'No items added yet.'}
          </div>
        </div>
      `;
    } else {
      // Pending aisle groups
      sortedAisles.forEach((aisle) => {
        itemsHtml += `
          <div class="aisle-group">
            <div class="aisle-title">${escapeHtml(aisle)}</div>
            <div class="aisle-items">
              ${groups[aisle].map((i) => this.renderItemRow(i, this.activeMode === 'plan' && isOwnerOrEditor, list.archived)).join('')}
            </div>
          </div>
        `;
      });

      // Skipped items section
      if (skipped.length > 0) {
        itemsHtml += `
          <div class="aisle-group">
            <div class="aisle-title" style="color:var(--amber);">⚠️ Not Collected (${skipped.length})</div>
            <div class="aisle-items">
              ${skipped.map((i) => this.renderItemRow(i, this.activeMode === 'plan' && isOwnerOrEditor, list.archived)).join('')}
            </div>
          </div>
        `;
      }

      // Checked items (in trolley)
      if (checked.length > 0) {
        itemsHtml += `
          <div class="aisle-group">
            <div class="aisle-title">In the Trolley (${checked.length})</div>
            <div class="aisle-items">
              ${checked.map((i) => this.renderItemRow(i, this.activeMode === 'plan' && isOwnerOrEditor, list.archived)).join('')}
            </div>
          </div>
        `;
      }
    }

    // Add Item Form (Visible in Plan mode or if list is empty)
    const showAddItemBox = isOwnerOrEditor && !list.archived && (this.activeMode === 'plan' || total === 0);
    const addItemHtml = showAddItemBox
      ? `
        <div class="add-item-box" id="add-item-container">
          <div class="add-item-row">
            <input type="text" class="input-field field-item" id="item-name-input" placeholder="Add item (e.g. Milk, Bananas)…" autocomplete="off" />
            <input type="text" class="input-field field-qty" id="item-qty-input" placeholder="1" value="1" title="Quantity" />
            <select class="input-field field-aisle" id="item-aisle-select">
              ${AISLE_ORDER.map((a) => `<option value="${a}">${a}</option>`).join('')}
            </select>
            <button class="btn btn-primary" id="add-item-submit-btn" style="padding:0.55rem 0.85rem;">+ Add</button>
          </div>
          <div class="suggestions-dropdown" id="suggestions-dropdown"></div>
        </div>
      `
      : '';

    // Action Bars based on Active Mode:
    // In Plan Mode: "Finalise Shopping List" bar (notifies shoppers)
    // In Shop Mode: "Finish Shop" floating bar (completes shopping trip)
    let bottomBarHtml = '';
    if (!list.archived && !list.shopDone && total > 0) {
      if (this.activeMode === 'plan' && isOwnerOrEditor) {
        bottomBarHtml = `
          <div class="finalize-shop-bar" id="finalize-list-bar">
            <div class="finalize-shop-text">
              <strong>${list.readyToShop ? '✓ List is Ready' : '✨ Finished editing?'}</strong>
              <div style="font-size:0.75rem;opacity:0.85;">Notify shoppers that the list is ready</div>
            </div>
            <button class="btn btn-sage" id="finalize-list-action-btn" style="padding:0.55rem 1.15rem;font-size:0.88rem;font-weight:600;border-radius:var(--radius-full);">
              ${list.readyToShop ? '📢 Re-Notify Shoppers' : '✨ Finalise Shopping List'}
            </button>
          </div>
        `;
      } else {
        bottomBarHtml = `
          <div class="finish-shop-bar" id="finish-shop-floating-bar">
            <div class="finish-shop-text">
              ${
                pending.length === 0
                  ? '<strong>🎉 All items collected!</strong>'
                  : `<strong>${pending.length}</strong> item${pending.length !== 1 ? 's' : ''} left to find`
              }
            </div>
            <button class="btn btn-amber" id="finish-shop-action-btn" style="padding:0.45rem 1rem;font-size:0.85rem;border-radius:var(--radius-full);">
              Finish Shop
            </button>
          </div>
        `;
      }
    }

    this.el.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:1rem;">
        <button class="btn btn-secondary" id="shopping-back-btn">
          ← Back to Lists
        </button>
        ${modeToggleHtml}
      </div>

      <div class="list-view-header">
        <h1 class="list-view-title">${escapeHtml(list.name)}</h1>
        <div class="list-view-meta">
          <span>${dateStr}</span>
          <span>•</span>
          <span>${done} of ${total} collected</span>
          ${this.activeMode === 'shop' ? '<span>•</span><span style="color:var(--sage);font-weight:600;">🛒 In-Store Mode</span>' : '<span>•</span><span style="color:var(--ink-secondary);font-weight:600;">📝 Plan &amp; Edit Mode</span>'}
        </div>
      </div>

      <!-- Meal Plan Photo Container -->
      <div id="meal-plan-mount"></div>

      ${bannerHtml}
      ${addItemHtml}

      <!-- Items Section -->
      <div id="items-list-container">
        ${itemsHtml}
      </div>

      ${bottomBarHtml}
    `;

    // Mode Toggle Buttons
    this.el.querySelector('#mode-shop-btn')?.addEventListener('click', () => {
      this.activeMode = 'shop';
      this.render();
    });

    this.el.querySelector('#mode-plan-btn')?.addEventListener('click', () => {
      this.activeMode = 'plan';
      this.render();
    });

    // Finalise List Button (Plan Mode)
    this.el.querySelector('#finalize-list-action-btn')?.addEventListener('click', async () => {
      try {
        await syncManager.flush();
        const res = await api.finalizeList(list.id);
        list.readyToShop = true;
        list.finalizedByName = user?.name || 'Editor';
        showToast('List finalised! 📢 Notification sent to shoppers ✓');
        // Switch to Shop Mode for instant visual feedback
        this.activeMode = 'shop';
        this.render();
        this.callbacks.onListUpdated();
      } catch (err: any) {
        showToast('Error finalising list: ' + err.message);
      }
    });

    // Finish shop button (Shop Mode)
    this.el.querySelector('#finish-shop-action-btn')?.addEventListener('click', async () => {
      const remaining = list.items?.filter((i) => !i.checked && !i.skipped) || [];
      if (remaining.length > 0) {
        const confirmFinish = confirm(
          `Finish shop early? ${remaining.length} item(s) are not collected and will be flagged for review.`
        );
        if (!confirmFinish) return;
      }

      try {
        await syncManager.flush();
        const uncollectedIds = remaining.map((r) => r.id);
        await api.finishShop(list.id, uncollectedIds);
        showToast('Shopping trip completed! 🎉');
        this.callbacks.onListUpdated();
      } catch (err: any) {
        showToast('Error finishing shop: ' + err.message);
      }
    });

    // Mount meal plan card
    const mpMount = this.el.querySelector('#meal-plan-mount');
    if (mpMount) {
      if (!this.mealPlanCard) {
        this.mealPlanCard = new MealPlanCard(list, isOwnerOrEditor, () => this.callbacks.onListUpdated());
      } else {
        this.mealPlanCard.update(list, isOwnerOrEditor);
      }
      mpMount.appendChild(this.mealPlanCard.getElement());
    }

    // Attach events
    this.el.querySelector('#shopping-back-btn')?.addEventListener('click', () => {
      syncManager.flush();
      this.callbacks.onBack();
    });

    // Item toggling (Clicking row works for EVERYONE: Owner, Editor, Shopper)
    this.el.querySelectorAll('.item-row').forEach((row) => {
      row.addEventListener('click', async (e) => {
        // Prevent trigger if delete button clicked
        if ((e.target as HTMLElement).closest('.item-delete-btn')) return;

        const itemId = row.getAttribute('data-id')!;
        const item = list.items?.find((i) => i.id === itemId);
        if (!item || item.skipped) return;

        // Toggle state locally immediately for instant tactile feedback
        item.checked = !item.checked;
        syncManager.queueItemUpdate(list.id, item.id, item.checked, item.skipped);
        this.render();

        // Background flush if online
        syncManager.flush();
      });
    });

    // Delete item (Available in Plan mode)
    this.el.querySelectorAll('.item-delete-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-id')!;
        try {
          await api.deleteItem(list.id, itemId);
          if (list.items) {
            list.items = list.items.filter((i) => i.id !== itemId);
          }
          showToast('Item removed');
          this.render();
        } catch (err: any) {
          showToast('Error removing item: ' + err.message);
        }
      });
    });

    // Autocomplete & Add Item (when form is visible)
    if (showAddItemBox) {
      this.setupAddItemEvents();
    }
  }

  private renderItemRow(item: ListItem, showDelete: boolean, archived: boolean): string {
    const qtyBadge = item.qty && item.qty !== '1' ? `<span class="item-qty-badge">${escapeHtml(item.qty)}</span>` : '';
    const deleteBtn =
      showDelete && !archived
        ? `<button class="item-delete-btn" data-id="${item.id}" title="Delete item">✕</button>`
        : '';

    let rowClass = 'item-row';
    let checkIcon = item.checked ? '✓' : '';

    if (item.checked) rowClass += ' checked';
    if (item.skipped) {
      rowClass += ' skipped';
      checkIcon = '⚠️';
    }

    return `
      <div class="${rowClass}" data-id="${item.id}">
        <div class="item-checkbox">${checkIcon}</div>
        <div class="item-content">
          <span class="item-name">${escapeHtml(item.name)}</span>
          ${qtyBadge}
        </div>
        ${deleteBtn}
      </div>
    `;
  }

  private setupAddItemEvents() {
    const nameInput = this.el.querySelector('#item-name-input') as HTMLInputElement;
    const qtyInput = this.el.querySelector('#item-qty-input') as HTMLInputElement;
    const aisleSelect = this.el.querySelector('#item-aisle-select') as HTMLSelectElement;
    const submitBtn = this.el.querySelector('#add-item-submit-btn');
    const dropdown = this.el.querySelector('#suggestions-dropdown') as HTMLElement;

    if (!nameInput) return;

    // Restore last aisle from localStorage
    const lastAisle = localStorage.getItem('lms_last_aisle');
    if (lastAisle && aisleSelect) aisleSelect.value = lastAisle;

    const onInput = () => {
      const val = nameInput.value.trim().toLowerCase();
      if (!val) {
        dropdown.classList.remove('open');
        return;
      }

      this.filteredSuggestions = (this.state.knownItems || [])
        .filter((k) => k.name.toLowerCase().includes(val) && k.name.toLowerCase() !== val)
        .slice(0, 6);

      if (this.filteredSuggestions.length === 0) {
        dropdown.classList.remove('open');
        return;
      }

      this.selectedSuggestionIdx = -1;
      dropdown.innerHTML = this.filteredSuggestions
        .map(
          (k, i) => `
            <div class="suggestion-item" data-idx="${i}">
              <span style="font-weight:500;">${escapeHtml(k.name)}</span>
              <span class="suggestion-aisle">${escapeHtml(k.aisle)}</span>
            </div>
          `
        )
        .join('');
      dropdown.classList.add('open');

      dropdown.querySelectorAll('.suggestion-item').forEach((itemEl) => {
        itemEl.addEventListener('mousedown', (e) => {
          e.preventDefault();
          const idx = parseInt(itemEl.getAttribute('data-idx')!);
          const pick = this.filteredSuggestions[idx];
          if (pick) {
            nameInput.value = pick.name;
            if (aisleSelect) aisleSelect.value = pick.aisle;
            dropdown.classList.remove('open');
            qtyInput?.focus();
          }
        });
      });
    };

    nameInput.addEventListener('input', onInput);
    nameInput.addEventListener('focus', onInput);

    nameInput.addEventListener('keydown', (e) => {
      const items = dropdown.querySelectorAll('.suggestion-item');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedSuggestionIdx = Math.min(this.selectedSuggestionIdx + 1, items.length - 1);
        items.forEach((el, i) => el.classList.toggle('highlighted', i === this.selectedSuggestionIdx));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedSuggestionIdx = Math.max(this.selectedSuggestionIdx - 1, -1);
        items.forEach((el, i) => el.classList.toggle('highlighted', i === this.selectedSuggestionIdx));
      } else if (e.key === 'Enter') {
        if (this.selectedSuggestionIdx >= 0 && this.filteredSuggestions[this.selectedSuggestionIdx]) {
          const pick = this.filteredSuggestions[this.selectedSuggestionIdx];
          nameInput.value = pick.name;
          if (aisleSelect) aisleSelect.value = pick.aisle;
          dropdown.classList.remove('open');
          qtyInput?.focus();
        } else {
          doAddItem();
        }
      } else if (e.key === 'Escape') {
        dropdown.classList.remove('open');
      }
    });

    qtyInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doAddItem();
    });

    const doAddItem = async () => {
      const name = nameInput.value.trim();
      const qty = qtyInput?.value.trim() || '1';
      const aisle = aisleSelect?.value || 'General';
      const list = this.state.currentList;

      if (!name || !list) {
        nameInput.focus();
        return;
      }

      dropdown.classList.remove('open');
      localStorage.setItem('lms_last_aisle', aisle);

      try {
        const res = await api.addItem(list.id, name, qty, aisle);
        if (!list.items) list.items = [];
        list.items.push(res.item);

        // Add to local known items
        const existingIdx = this.state.knownItems.findIndex((k) => k.name.toLowerCase() === name.toLowerCase());
        if (existingIdx >= 0) {
          this.state.knownItems[existingIdx].aisle = aisle;
        } else {
          this.state.knownItems.push({ name, aisle, usageCount: 1 });
        }

        nameInput.value = '';
        if (qtyInput) qtyInput.value = '1';
        nameInput.focus();
        this.render();
      } catch (err: any) {
        showToast('Error adding item: ' + err.message);
      }
    };

    submitBtn?.addEventListener('click', doAddItem);
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
